import React, { useState, useEffect } from 'react';
import { Modal, Card, Button, Input, Space, Empty } from 'antd';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import { 
  PictureOutlined, 
  SearchOutlined,
  EyeOutlined,
  CheckOutlined
} from '@ant-design/icons';

const { Search } = Input;

const ImageSelectorModal = ({ 
  visible, 
  onClose, 
  onSelect, 
  attachments = [],
  displayFiles = []
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);

  // 合并所有附件，筛选出图片类型
  const allImages = React.useMemo(() => {
    const allFiles = [...(attachments || []), ...(displayFiles || [])];
    return allFiles.filter(file => {
      const contentType = file.content_type || file.type || '';
      const fileName = file.file_name || file.name || '';
      
      // 检查是否为图片类型
      return contentType.startsWith('image/') || 
             /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(fileName);
    });
  }, [attachments, displayFiles]);

  // 根据搜索文本过滤图片
  const filteredImages = React.useMemo(() => {
    if (!searchText.trim()) {
      return allImages;
    }
    
    return allImages.filter(image => {
      const fileName = image.file_name || image.name || '';
      const description = image.description || '';
      return fileName.toLowerCase().includes(searchText.toLowerCase()) ||
             description.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [allImages, searchText]);

  // 处理图片选择
  const handleImageSelect = (image) => {
    setSelectedImage(image);
  };

  // 处理确认选择
  const handleConfirm = () => {
    if (!selectedImage) {
      window.message?.warning('请先选择一张图片');
      return;
    }

    // 生成Markdown图片语法
    const imageUrl = selectedImage.url || '';
    const imageName = selectedImage.file_name || selectedImage.name || '图片';
    const imageMarkdown = `![${imageName}](${imageUrl})`;

    // 调用父组件的选择回调
    if (onSelect) {
      onSelect(imageMarkdown, selectedImage);
    }

    // 重置状态并关闭Modal
    setSelectedImage(null);
    setSearchText('');
    onClose();
  };

  // 处理取消
  const handleCancel = () => {
    setSelectedImage(null);
    setSearchText('');
    onClose();
  };

  // 渲染图片卡片
  const renderImageCard = (image, index) => {
    const isSelected = selectedImage && selectedImage.id === image.id;
    const imageUrl = image.url || '';
    const imageName = image.file_name || image.name || '未命名图片';
    const imageSize = image.size ? `${(image.size / 1024).toFixed(1)} KB` : '';

    return (
      <Card
        key={`${image.id || index}`}
        hoverable
        className={`cursor-pointer transition-all duration-200 ${
          isSelected 
            ? 'ring-2 ring-blue-500 border-blue-500' 
            : 'hover:shadow-md'
        }`}
        onClick={() => handleImageSelect(image)}
        bodyStyle={{ padding: '12px' }}
      >
        <div className="relative">
          {/* 图片预览 */}
          <div className="w-full h-32 bg-gray-100 rounded-lg overflow-hidden mb-2 flex items-center justify-center">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={imageName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className="w-full h-full flex items-center justify-center text-gray-400"
              style={{ display: imageUrl ? 'none' : 'flex' }}
            >
              <PictureOutlined className="text-2xl" />
            </div>
          </div>

          {/* 选择指示器 */}
          {isSelected && (
            <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
              <CheckOutlined className="text-white text-sm" />
            </div>
          )}

          {/* 图片信息 */}
          <div className="text-center">
            <div className="text-sm font-medium text-gray-900 truncate mb-1">
              {imageName}
            </div>
            {imageSize && (
              <div className="text-xs text-gray-500">
                {imageSize}
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <PictureOutlined className="text-blue-500" />
          <span>选择图片</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          取消
        </Button>,
        <Button 
          key="confirm" 
          type="primary" 
          onClick={handleConfirm}
          disabled={!selectedImage}
          className="!text-black"
        >
          确认选择
        </Button>
      ]}
    >
      <div className="space-y-4">
        {/* 搜索框 */}
        <div className="flex items-center gap-2">
          <Search
            placeholder="搜索图片..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            className="flex-1"
          />
          <span className="text-sm text-gray-500">
            共 {filteredImages.length} 张图片
          </span>
        </div>

        {/* 图片网格 */}
        <div className="max-h-96 overflow-y-auto">
          {filteredImages.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredImages.map((image, index) => renderImageCard(image, index))}
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                searchText ? `未找到包含"${searchText}"的图片` : "暂无图片"
              }
              className="py-8"
            />
          )}
        </div>

        {/* 使用说明 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-sm text-blue-800">
            <div className="font-medium mb-1">使用说明：</div>
            <ul className="text-xs space-y-1">
              <li>• 点击图片卡片选择要插入的图片</li>
              <li>• 选中的图片会显示蓝色边框和勾选标记</li>
              <li>• 确认选择后，图片会自动插入到Markdown编辑器的光标位置</li>
              <li>• 如果没有光标位置，图片会插入到文档末尾</li>
            </ul>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ImageSelectorModal;
